import * as R from "ramda";

import { arrayWrap, sortByDateAttr } from "utils/func";
import { parseTime } from "utils/time";

export const getDescriptiveID = R.prop("descriptive_id");
export const getRequirementName = R.prop("requirement_name");
export const getStateHistory = R.pipe(R.propOr([], "state_history"), R.values);
export const getLastTransition = R.pipe(getStateHistory, R.last);
export const getCurrentState = R.pipe(getLastTransition, R.propOr("in_progress", "to_state"));
export const getHistoryState = R.prop("to_state");
export const getSubmittedAt = R.prop("submitted_at");
export const getRequirementID = R.prop("requirement_id");
export const getAssignedUserIDs = R.propOr([], "assigned_user_ids");
export const getCurrentHistoryState = R.pipe(getStateHistory, R.last, getHistoryState);

const hasState = (to_state, app) => R.any(R.propEq(to_state, "to_state"), getStateHistory(app));
const hasAnyState = (to_states, app) =>
  R.any(R.compose(R.includes(R.__, to_states), R.prop("to_state")), getStateHistory(app));

const STATES = [
  "in_progress",
  "processed",
  "submitted",
  "rejected",
  "issued",
  "approved",
  "payable",
  "revoked",
  "no_longer_required",
  "submitted_external",
  "under_review",
];
export const [
  IN_PROGRESS,
  PROCESSED,
  SUBMITTED,
  REJECTED,
  ISSUED,
  APPROVED,
  PAYABLE,
  REVOKED,
  NO_LONGER_REQUIRED,
  SUBMITTED_EXTERNAL,
  UNDER_REVIEW,
] = STATES;

export const REJECTED_STATES = [REJECTED, REVOKED];
export const APPROVED_STATES = [ISSUED, PROCESSED];
export const SUBMITTED_STATES = [SUBMITTED, SUBMITTED_EXTERNAL];

export const getIssuedAt = R.pipe(
  getStateHistory,
  R.find(R.propEq(ISSUED, "to_state")),
  R.prop("created_at"),
  parseTime,
);

export const isInProgress = R.pipe(getCurrentState, R.equals(IN_PROGRESS));
export const isSubmitted = R.pipe(getCurrentState, R.includes(R.__, SUBMITTED_STATES));
export const isUnsubmitted = R.complement(isSubmitted);
export const isRejected = R.pipe(getCurrentState, R.equals(REJECTED));
export const isIssued = R.pipe(getCurrentState, R.equals(ISSUED));
export const isApproved = R.pipe(getCurrentState, R.equals(APPROVED));
export const isPayable = R.pipe(getCurrentState, R.equals(PAYABLE));
export const isRevoked = R.pipe(getCurrentState, R.equals(REVOKED));
export const isNoLongerRequired = R.pipe(getCurrentState, R.equals(NO_LONGER_REQUIRED));

export const wasSubmitted = R.curry(hasAnyState)(SUBMITTED_STATES);
export const wasIssued = R.curry(hasState)(ISSUED);
export const wasProcessed = R.curry(hasState)(PROCESSED);

export const getStateMetadata = R.curry((attrs, state) =>
  R.path(R.prepend("metadata", arrayWrap(attrs)), state),
);

export const getTransactionID = R.pipe(getLastTransition, getStateMetadata("transaction_id"));
export const getInvoiceID = R.pipe(R.propOr([], "unpaid_invoice_ids"), R.head);

export const getDisplayID = (ra) => R.prop("display_id", ra) || ra.id;

export const getReviewResults = (requirementApplication, reviews) => {
  const applicationReviews = sortByDateAttr(
    R.propOr([], "application_reviews", requirementApplication),
    "created_at",
  );

  return R.map((review) => {
    const applicationReview = R.find(
      R.propEq(review.id, "requirement_review_id"),
      applicationReviews,
    );
    const result = R.mergeRight(review, {
      created_at: R.prop("created_at", applicationReview),
      reviewedBy: R.prop("reviewer_full_name", applicationReview),
      result: R.propOr("pending", "result", applicationReview),
    });
    return result;
  }, reviews);
};

const COMPLETED_REVIEW_STATES = ["approve", "reject"];
export const isCompletedReview = (review) => R.includes(review.result, COMPLETED_REVIEW_STATES);
export const getPendingReviews = R.pipe(getReviewResults, R.reject(isCompletedReview));

export const allowUndo = R.pipe(getCurrentState, R.includes(R.__, [UNDER_REVIEW, REJECTED]));
